import { type Writable, writable, get } from "svelte/store";
import type { FrameElement } from "../../lib/layeredCanvas/dataModels/frameTree";
import { calculatePhysicalLayout, findLayoutOf } from "../../lib/layeredCanvas/dataModels/frameTree";
import { Film } from "../../lib/layeredCanvas/dataModels/film";
import { ImageMedia } from "../../lib/layeredCanvas/dataModels/media";
import type { Page } from '../../lib/book/book';
import { trapezoidBoundingRect } from "../../lib/layeredCanvas/tools/geometry/trapezoid";
import { minimumBoundingScale } from "../../lib/layeredCanvas/tools/geometry/geometry";
import { outPaintFilmInline, calculateFramePadding } from '../../utils/outPaintFilm';
import { toastStore } from '@skeletonlabs/skeleton';
import { onlineStatus } from "../../utils/accountStore";
import { get as getStore } from 'svelte/store';
import { _ } from 'svelte-i18n';
import { commit } from '../operations/commitOperations';
import type { FilmOperationTarget } from '../operations/filmStackOperations';
import type { GeneratedFilmResult } from "../../generator/imageGeneratorStore";
import {
  setupCommandSubscription,
  handleScribbleCommand,
  handleGenerateCommand,
  handlePunchCommand,
  handleUpscaleCommand,
  handleVideoCommand,
  processCommand,
  handleCoverCommand,
  handleEraserCommand,
  handleInpaintCommand,
  handleTextEditCommand,
  handleAngleEditCommand,
  handleSendToMaterialCollectionCommand,
  handleTextLiftCommand,
  handleLayerizeCommand,
  handleMangaLayerizeCommand,
} from '../operations/filmStackOperations';

type FrameInspectorCommand = "generate" | "cover" | "scribble" | "punch" | "outpaint" | "video" | "upscale" | "eraser" | "inpaint" | "textedit" | "angleedit" | "sendToMaterialCollection" | "textlift" | "layerize" | "manga-layerize";

export interface FrameInspectorTarget extends FilmOperationTarget {
  frame: FrameElement;
  command: FrameInspectorCommand | null;
}

export const frameInspectorTarget: Writable<FrameInspectorTarget | null> = writable(null);
export const frameInspectorRebuildToken: Writable<number> = writable(0);

// コマンド実行に必要なツールへの参照
let painterRunWithFrame: ((page: Page, frame: FrameElement, film: Film) => Promise<void>) | null = null;
let runImageGenerator: ((prompt: string, filmStack: any, gallery: any, frameSize: [number, number]) => Promise<GeneratedFilmResult | null>) | null = null;

// サブスクリプション解除用の関数参照用オブジェクト
const unsubscribeRef = { value: null as Function | null };

// ツール参照を設定
export function setFrameCommandTools(
  _painterRunWithFrame: (page: Page, frame: FrameElement, film: Film) => Promise<void>,
  _runImageGenerator: (prompt: string, filmStack: any, gallery: any, frameSize: [number, number]) => Promise<GeneratedFilmResult | null>
) {
  painterRunWithFrame = _painterRunWithFrame;
  runImageGenerator = _runImageGenerator;
  
  // 共通ライブラリを使用してサブスクリプション設定
  setupCommandSubscription<FrameInspectorTarget>(
    frameInspectorTarget,
    onFrameCommand,
    unsubscribeRef
  );
}

// コマンド処理関数
async function onFrameCommand(fit: FrameInspectorTarget | null) {
  if (!fit || fit.command === null) return;
  
  if (!painterRunWithFrame || !runImageGenerator) {
    console.error("Frame command tools not initialized");
    return;
  }

  // 共通ライブラリを使用してコマンド処理
  await processCommand<FrameInspectorTarget>(fit, frameInspectorTarget, {
    "cover": async (target) => handleCoverCommand(
      target,
      () => {
        const pageLayout = calculatePhysicalLayout(target.page.frameTree, target.page.paperSize, [0,0]);
        const leafLayout = findLayoutOf(pageLayout, target.frame);
        const frameRect = trapezoidBoundingRect(leafLayout!.corners);
        const targetSize: [number,number] = [
          Math.ceil(frameRect[2] / 128) * 128,
          Math.ceil(frameRect[3] / 128) * 128
        ];
        return targetSize;
      },
      frameInspectorTarget,
    ),
    "eraser": handleEraserCommand,
    "inpaint": handleInpaintCommand,
    "textedit": handleTextEditCommand,
    "angleedit": handleAngleEditCommand,
    "textlift": handleTextLiftCommand,
    "scribble": async (target) => handleScribbleCommand(target, painterRunWithFrame!, target.frame),
    "generate": async (target) => {
      const pageLayout = calculatePhysicalLayout(target.page.frameTree, target.page.paperSize, [0,0]);
      const leafLayout = findLayoutOf(pageLayout, target.frame);
      const frameRect = trapezoidBoundingRect(leafLayout!.corners);
      const frameSize: [number, number] = [frameRect[2], frameRect[3]];
      return handleGenerateCommand(
        target,
        fit.frame.prompt ?? "",
        runImageGenerator!,
        (film, target) => {
          return minimumBoundingScale(film.getContentSize(target.page.paperSize), frameSize);
        },
        frameInspectorTarget,
        target.frame,
        frameSize
      );
    },
    "punch": handlePunchCommand,
    "upscale": handleUpscaleCommand,
    "outpaint": outPaintFrameFilm,
    "video": handleVideoCommand,
    "sendToMaterialCollection": handleSendToMaterialCollectionCommand,
    "layerize": handleLayerizeCommand,
    "manga-layerize": handleMangaLayerizeCommand,
  });
  
  frameInspectorRebuildToken.update(v => v + 1);
}

// アウトペインティング処理（フレーム特有の機能）
async function outPaintFrameFilm(fit: FrameInspectorTarget) {
  if (get(onlineStatus) !== 'signed-in') {
    toastStore.trigger({ message: getStore(_)('frame.errors.notLoggedIn'), timeout: 3000});
    return;
  }

  const film = fit.commandTargetFilm!;
  if (film.content.kind !== 'media' || !(film.content.media instanceof ImageMedia)) { return; }

  const padding = calculateFramePadding(fit.page, fit.frame, film);
  const newFilm = outPaintFilmInline(film, padding);
  if (newFilm) {
    const index = fit.frame.filmStack.films.indexOf(film);
    fit.frame.filmStack.films.splice(index + 1, 0, newFilm);
    commit(null);
  }
}

// modalFrameVideo関数は共通化された handleVideoCommand に置き換えられました
